import { createContext, useContext, type ReactNode } from 'react';

/**
 * VERA-315 — how a `DrawerPopup` PRESENTS itself at desktop width.
 *
 * `apps/app` is a phone surface (a tag is tapped with a phone, working-conventions
 * §37), so every `DrawerPopup` is an `anchor='bottom'` sheet and that is correct at
 * 375. On a 1280-wide window the same sheet spans the whole window and slides up off
 * the bottom edge, which reads as a phone control stranded on a desktop. A bottom
 * sheet is a PHONE idiom; the desktop equivalent is a centred modal.
 *
 * ## The default is now ON, and that inversion is the point
 *
 * This shipped as `createContext(false)` — the desktop presentation OPTED INTO per
 * subtree. Exactly one page (the certificate page) ever provided it, so the app's
 * other 34 files carrying a `DrawerPopup` silently kept the phone idiom at every
 * width: the Log out confirm still arrived at 1280 as a full-window bottom sheet
 * WITH A GRAB HANDLE, advertising a swipe gesture a mouse cannot perform.
 *
 * That is the identical failure mode working-conventions §42 records for referrer
 * state — an opt-in prop that 2 of 37 call sites remembered — and §47's standing
 * point that the default direction of improvement is DOWN into the shared component,
 * not sideways per surface. An opt-in that almost nobody opts into is not a
 * convention; it is a latent bug with a switch next to it.
 *
 * So the default is `true`: a bottom sheet becomes a centred dialog at md+ (720px in
 * this app — `settings/mui/breakpoints.ts`, NOT MUI's 900) everywhere, and a surface
 * that genuinely wants a sheet on a desktop says so explicitly with `MobileSheetOnly`
 * below. Nothing at 375 changes in either direction: `DrawerPopup` only consults this
 * above `md`.
 *
 * ## What the flip does NOT touch
 *
 * `DrawerPopup` gates the dialog branch on `anchor === 'bottom' && !fullScreen`, so
 * three families are unaffected by construction and need no opt-out:
 *   - deliberate side panels (`anchor='right'` — the certificates / scans /
 *     provenance / activity Filters, which already present correctly at desktop);
 *   - `fullScreen` sheets (the legal-document drawer), which own their own scroll
 *     region and would break under a height-capped dialog;
 *   - sheets already gated behind an `isMobile` check, which never mount at md+
 *     (`MobileDateRangeDrawer` and all three of its call sites).
 */
export const DesktopSheetContext = createContext(true);

export const useDesktopSheet = () => useContext(DesktopSheetContext);

/**
 * The explicit opt-OUT: keep the bottom sheet at every width for this subtree.
 *
 * A component rather than a bare `<DesktopSheetContext.Provider value={false}>` so
 * the intent is greppable and reads as a decision at the call site. There is no user
 * of it today — every surface audited in the VERA-315 sweep is better as a dialog on
 * a desktop — and that is deliberate: it exists so a surface that genuinely needs the
 * phone idiom at md+ has a stated way to say so, rather than the next author
 * reintroducing a per-sheet prop.
 */
export const MobileSheetOnly = ({ children }: { children: ReactNode }) => (
  <DesktopSheetContext.Provider value={false}>{children}</DesktopSheetContext.Provider>
);
